'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useRole } from '@/context/RoleContext';
import RoleSwitcher from '@/components/RoleSwitcher';
import { LayoutDashboard, Clock, FileText, Users, CalendarDays, BarChart3, Settings, ChevronLeft, ChevronRight, Building2, Bell, LogOut, ShieldCheck, Lock } from 'lucide-react';

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const { currentUser, permissions } = useRole();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  // Build nav groups based on permissions
  const operationsItems = [
    permissions.canViewDashboard && { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
    permissions.canViewTimeClock && { href: '/time-clock', label: 'Time Clock', icon: Clock, badge: permissions.canApproveTimesheets ? '4' : null },
    permissions.canViewPayroll && { href: '/payroll-details', label: 'Payroll', icon: FileText, badge: null },
    // Employee self-service
    !permissions.canViewDashboard && permissions.canViewOwnPayslip && { href: '/my-payslips', label: 'My Payslips', icon: FileText, badge: null },
    !permissions.canViewDashboard && permissions.canViewOwnTimesheets && { href: '/my-timesheets', label: 'My Timesheets', icon: Clock, badge: null },
  ].filter(Boolean) as { href: string; label: string; icon: React.ElementType; badge: string | null }[];

  const workforceItems = [
    permissions.canAccessWorkers && { href: '/workers', label: 'Workers', icon: Users, badge: null },
    permissions.canAccessLeave && { href: '/leave', label: 'Leave', icon: CalendarDays, badge: permissions.canApproveTimesheets ? '2' : null },
  ].filter(Boolean) as { href: string; label: string; icon: React.ElementType; badge: string | null }[];

  const financeItems = [
    permissions.canAccessReports && { href: '/reports', label: 'Reports', icon: BarChart3, badge: null },
    permissions.canAccessCompliance && { href: '/compliance', label: 'Compliance', icon: ShieldCheck, badge: '1' },
  ].filter(Boolean) as { href: string; label: string; icon: React.ElementType; badge: string | null }[];

  const NAV_GROUPS = [
    operationsItems.length > 0 && { label: 'Operations', items: operationsItems },
    workforceItems.length > 0 && { label: 'Workforce', items: workforceItems },
    financeItems.length > 0 && { label: 'Finance', items: financeItems },
  ].filter(Boolean) as { label: string; items: { href: string; label: string; icon: React.ElementType; badge: string | null }[] }[];

  return (
    <aside
      className={`
        relative flex flex-col h-screen bg-card border-r border-border
        transition-all duration-300 ease-in-out shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-3 ${collapsed ? 'justify-center' : 'gap-2.5 px-4'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-[15px] tracking-tight text-foreground truncate">
            CarePayroll
          </span>
        )}
      </div>

      {/* Workspace badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-lg bg-secondary flex items-center gap-2">
          <Building2 size={14} className="text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] font-500 text-muted-foreground leading-none mb-0.5">Organisation</p>
            <p className="text-[12px] font-600 text-foreground truncate leading-none">Sunrise Care Agency</p>
            <p className="text-[10px] font-500 text-success flex items-center gap-1 mt-0.5 leading-none">
              <span className="w-1.5 h-1.5 rounded-full bg-success inline-block shrink-0" />
              CQC Reg: 1-123456789 · Rated: Good
            </p>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-2 px-2">
        {NAV_GROUPS.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1.5">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={`nav-${item.href}`}>
                    <Link
                      href={item.href}
                      className={`
                        group relative flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-500
                        transition-all duration-150
                        ${active
                          ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-secondary hover:text-foreground'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon
                        size={18}
                        className={`shrink-0 ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
                      />
                      {!collapsed && (
                        <span className="flex-1 truncate">{item.label}</span>
                      )}
                      {!collapsed && item.badge && (
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-primary/10 text-primary text-[10px] font-700 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary" />
                      )}
                      {collapsed && (
                        <span className="
                          absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-card text-xs font-500
                          opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50
                          transition-opacity duration-150
                        ">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-0.5">
        {/* Role Switcher */}
        <div className="mb-1">
          <RoleSwitcher collapsed={collapsed} />
        </div>

        <button className={`group relative w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-500 text-secondary-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}>
          <Bell size={18} className="text-muted-foreground group-hover:text-foreground shrink-0" />
          {!collapsed && <span className="flex-1 text-left">Notifications</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-card text-xs font-500 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
              Notifications
            </span>
          )}
        </button>

        {permissions.canAccessSettings && (
          <button className={`group relative w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-500 text-secondary-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}>
            <Settings size={18} className="text-muted-foreground group-hover:text-foreground shrink-0" />
            {!collapsed && <span className="flex-1 text-left">Settings</span>}
            {collapsed && (
              <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-foreground text-card text-xs font-500 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                Settings
              </span>
            )}
          </button>
        )}

        {/* UK GDPR badge */}
        {!collapsed && (
          <div className="mx-1 mb-1 px-2.5 py-1.5 rounded-lg bg-success/6 border border-success/15 flex items-center gap-1.5">
            <Lock size={10} className="text-success shrink-0" />
            <span className="text-[10px] font-500 text-success leading-none">UK GDPR Compliant · ICO: ZA987654</span>
          </div>
        )}

        {/* User */}
        <div className={`flex items-center gap-2.5 px-2.5 py-2 mt-1 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-[11px] font-700 text-primary">{currentUser.initials}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-600 text-foreground truncate leading-none mb-0.5">{currentUser.name}</p>
              <p className="text-[11px] text-muted-foreground truncate leading-none">{currentUser.roleLabel}</p>
            </div>
          )}
          {!collapsed && (
            <button className="text-muted-foreground hover:text-danger transition-colors duration-150 shrink-0">
              <LogOut size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-[72px] w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all duration-150 z-10 shadow-sm"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}